import { css } from "@emotion/css";
import { Tooltip } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import type { DeploymentFreezeListPage, DeploymentFreezeSummary } from "@octopusdeploy/octopus-server-client";
import { DeploymentFreezeStatus } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import React from "react";
import { useParams } from "react-router";
import Chip from "~/components/Chips/Chip";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import InternalLink from "~/components/Navigation/InternalLink/index";
import { DataTableRowColumn } from "~/primitiveComponents/dataDisplay/DataTable/index";
import DateFormatter from "~/utils/DateFormatter/index";
interface DeploymentFreezeDataTableNameCellProps {
    freeze: DeploymentFreezeSummary;
    freezeListPage: DeploymentFreezeListPage;
    hasMissingResource?: boolean;
}
export const DeploymentFreezeDataTableNameCell = ({ freezeListPage, hasMissingResource, freeze }: DeploymentFreezeDataTableNameCellProps) => {
    const routeParams = useParams<{
        spaceId: string | undefined;
        projectSlug: string | undefined;
    }>();
    const { Id: id, Name: name, Start: start, End: end, Status: status, OwnerId: ownerId, SpaceId: spaceId } = freeze;
    const isProjectFreezeEnabled = isFeatureToggleEnabled("ProjectDeploymentFreezesFeatureToggle");
    const isProjectFreezeListPage = freezeListPage === "project";
    const isProjectLevelFreeze = isProjectFreezeEnabled && !isProjectFreezeListPage && ownerId != undefined;
    const isGlobalLevelFreeze = isProjectFreezeEnabled && isProjectFreezeListPage && ownerId == undefined;
    const openNewTab = isProjectLevelFreeze || isGlobalLevelFreeze;
    const dateString = () => {
        switch (status) {
            case DeploymentFreezeStatus.Active:
                return `Will end on ${DateFormatter.dateToLongFormat(end)}`;
            case DeploymentFreezeStatus.Scheduled:
                return `Will start on ${DateFormatter.dateToLongFormat(start)}`;
            case DeploymentFreezeStatus.Expired:
                return `Ended on ${DateFormatter.dateToLongFormat(end)}`;
        }
    };
    const getFreezeEditUrl = () => {
        if (!isProjectFreezeEnabled) {
            return links.deploymentFreezesEditPage.generateUrl({ deploymentFreezeId: id });
        }
        if (ownerId) {
            return links.editProjectDeploymentFreezePage.generateUrl({ spaceId: spaceId ?? routeParams.spaceId, projectSlug: ownerId, deploymentFreezeId: id });
        }
        return links.deploymentFreezesEditPage.generateUrl({ deploymentFreezeId: id });
    };
    return (<DataTableRowColumn className={cellStyles}>
            <div className={nameCellStyles}>
                {isGlobalLevelFreeze && (<Chip noMargin={true} description={!hasMissingResource ? "This freeze has been set at the instance level. Clicking will redirect you to the instance-level editing page." : ""}>
                        Global Freeze
                    </Chip>)}
                {isProjectLevelFreeze && (<Chip noMargin={true} description={!hasMissingResource ? "This freeze has been set at the project level. Clicking will redirect you to the project-level editing page." : ""}>
                        Project Freeze
                    </Chip>)}
                {!hasMissingResource ? (<InternalLink to={getFreezeEditUrl()} openInSelf={!openNewTab}>
                        <div className={nameStyles}>{name}</div>
                    </InternalLink>) : (<div className={nameStyles}>
                        <Tooltip content={"You don't have permission to access one or more projects/tenants/environments connected to this freeze. Please contact your Octopus Administrator to request required permissions or create a new freeze."}>
                            {name}
                        </Tooltip>
                    </div>)}
                <div className={dateStyles}>{dateString()}</div>
            </div>
        </DataTableRowColumn>);
};
const nameCellStyles = css({
    padding: `${space[16]}, ${space[8]}`,
});
const nameStyles = css({
    padding: `${space[4]} 0`,
});
const dateStyles = css({
    fontSize: "12px",
});
const cellStyles = css({ width: "35%", overflowWrap: "anywhere" });
